import { atom } from "jotai";

// Curated font keys — must stay in sync with the order-handler FontKey union.
export type BrandingFontKey = 'inter' | 'playfair' | 'dm-sans' | 'lora' | 'montserrat' | 'cormorant';

// #region IInteriorBranding
// Resolved branding (server applies per-field defaults, so nothing is missing).
export interface IInteriorBranding {
    logo_url: string | null;
    cover_image_url: string | null;
    primary_color: string;
    accent_color: string;
    font_key: BrandingFontKey;
    welcome_headline: string | null;
    welcome_body: string | null;
    contact_name: string | null;
    contact_email: string | null;
    contact_phone: string | null;
    contact_website: string | null;
}

// #region IInteriorBrandingPayload
export type IInteriorBrandingPayload = Partial<IInteriorBranding>;

// #region FontOption
export interface FontOption {
    key: BrandingFontKey;
    name: string;
    // CSS stack applied through the --share-font custom property.
    stack: string;
    // Google Fonts stylesheet for the family (loaded on demand, deduped).
    href: string;
}

// #region FONT_OPTIONS
export const FONT_OPTIONS: FontOption[] = [
    {
        key: 'inter',
        name: 'Inter — clean & modern',
        stack: "'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif",
        href: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap'
    },
    {
        key: 'dm-sans',
        name: 'DM Sans — friendly geometric',
        stack: "'DM Sans', system-ui, -apple-system, 'Segoe UI', sans-serif",
        href: 'https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&display=swap'
    },
    {
        key: 'montserrat',
        name: 'Montserrat — bold & contemporary',
        stack: "'Montserrat', system-ui, -apple-system, 'Segoe UI', sans-serif",
        href: 'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&display=swap'
    },
    {
        key: 'playfair',
        name: 'Playfair Display — editorial serif',
        stack: "'Playfair Display', Georgia, 'Times New Roman', serif",
        href: 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&display=swap'
    },
    {
        key: 'lora',
        name: 'Lora — warm serif',
        stack: "'Lora', Georgia, 'Times New Roman', serif",
        href: 'https://fonts.googleapis.com/css2?family=Lora:wght@400;500;600;700&display=swap'
    },
    {
        key: 'cormorant',
        name: 'Cormorant — refined & luxe',
        stack: "'Cormorant Garamond', Georgia, 'Times New Roman', serif",
        href: 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600;700&display=swap'
    }
];

// #region atoms
// Saved branding as last returned by the API (null until fetched).
export const interiorBrandingAtom = atom<IInteriorBranding | null>(null);

// Unsaved editor state — this is what the live preview renders.
export const brandingDraftAtom = atom<IInteriorBranding | null>(null);

export const brandingLoadingAtom = atom<boolean>(false);

export const brandingSavingAtom = atom<boolean>(false);
